import { FC } from 'react';
import { Icon, IconName } from 'src/components';
import * as S from './styles';
import type { PathProgressTrackSize } from './styles';

export type { PathProgressTrackSize };

export type PathProgressTrackProps = {
  size?: PathProgressTrackSize;
  progressRatio: number;
  totalSessions: number;
  checkpointIcon: IconName;
  startLabel?: string;
  checkpointLabel?: string;
  youAreHereLabel?: string;
  trackTestId?: string;
  fillTestId?: string;
  dotsTestId?: string;
  hereMarkerTestId?: string;
};

/**
 * Shared start → sessions → checkpoint track used by the Home Path card and
 * the learn end-of-session progress card. `home` is the full-size track with
 * name pills; `compact` is the reduced EOS variant.
 */
export const PathProgressTrack: FC<PathProgressTrackProps> = ({
  size = 'home',
  progressRatio,
  totalSessions,
  checkpointIcon,
  startLabel,
  checkpointLabel,
  youAreHereLabel,
  trackTestId,
  fillTestId,
  dotsTestId,
  hereMarkerTestId,
}) => {
  const showHereMarker =
    size === 'home' ? progressRatio > 0 : progressRatio > 0 && progressRatio < 1;

  return (
    <S.ProgressSection $size={size} aria-hidden>
      <S.TrackRow $size={size}>
        <S.Track $size={size} data-testid={trackTestId}>
          <S.TrackFill $ratio={progressRatio} data-testid={fillTestId} />
        </S.Track>
        {totalSessions > 0 && (
          <S.DotsRow data-testid={dotsTestId}>
            {Array.from({ length: totalSessions }, (_, index) => (
              <S.LessonDot key={index} $size={size} $ratio={index / totalSessions} />
            ))}
          </S.DotsRow>
        )}
        <S.StartNodeWrap $size={size}>
          <S.StartNode $size={size}>
            <Icon name="check" />
          </S.StartNode>
          {startLabel ? <S.StartPill>{startLabel}</S.StartPill> : null}
        </S.StartNodeWrap>
        {showHereMarker && (
          <S.HereMarker $size={size} $ratio={progressRatio} data-testid={hereMarkerTestId}>
            {size === 'home' && (
              <>
                {youAreHereLabel ? <S.HerePill>{youAreHereLabel}</S.HerePill> : null}
                <S.HereDot />
              </>
            )}
          </S.HereMarker>
        )}
        <S.CheckpointNodeWrap $size={size}>
          <S.CheckpointNode $size={size}>
            <Icon name={checkpointIcon} />
          </S.CheckpointNode>
          {checkpointLabel ? <S.CheckpointPill>{checkpointLabel}</S.CheckpointPill> : null}
        </S.CheckpointNodeWrap>
      </S.TrackRow>
    </S.ProgressSection>
  );
};
